// ============================================================
// StatementPanel.tsx — B4: the case's SETTLEMENT / COMPLETION STATEMENT, at
// the foot of the Settlement section (backend settlementStatementController).
//   • the draft: settlement date, damages received date, interim / final,
//     retention + reason, where the net goes (+ detail), who signs — read
//     first (a summary + pencil), Save / Cancel only while editing;
//   • why it can't issue yet — each reason in plain words with a button that
//     does the fix (server-enforced: Issue refuses while any holds);
//   • maker / checker: "Mark as prepared" (fee earner) → "Approve" (an admin /
//     bill approver, not the preparer — ticking "I've reviewed these notes"
//     when there are ADVISORY notes, which never block) → "Issue statement";
//     once prepared the details lock — "Send back for changes" unlocks them;
//   • a child's / protected party's statement can issue as PROPOSED before the
//     court order (for the litigation friend + the approval hearing); the
//     final statement is the next version, once the order is recorded;
//   • versions: v1 Issued … "Superseded by v2"; a correction = a new version;
//   • an issued statement: preview / print, send (as bills), post / hand,
//     the client's answer (adults: AGREED / DISPUTED + amount), the court's
//     approval (children / protected parties), and the B4b hand-off —
//     "Ready for client account" with the net, the deductions and anything held.
//
// (Batch 2) Split into statement/*: DraftEditor (read-first details, lock +
// send back, the action box, maker / checker), CourtApproval (court order
// summary + step tracker), IssuedStatement (issued summary card, the
// client's answer, hand-off, versions). This file loads the case's
// statements, runs each write (busy, error, the fresh list — or a re-read on
// a `{ saved: true, reload: true }` reply), tells the rest of the screen
// (emitCaseChange) and re-reads when another part changes what it shows
// (useCaseChange — audit M15).
// (B4c batch 1b) E-signature (pilot): the send window's "Send for
// e-signature", and on the issued card the request's status with Resend /
// Cancel / View signed PDF / Evidence pack.
// (E-sign audit fixes A) Issuing / correcting cancels the old version's open
// signing links; if any couldn't be cancelled the reply says so
// (esignRevokeWarning) — shown as a warning after that action.
// (E-sign fixes B) LIVE for everyone viewing the case (hooks/useCaseLive):
// a 'case-changed' (topics settlement / esign) or a reconnect re-reads the
// statements — the status chip, "Signed online by…", the client's answer,
// the money split and the e-sign status update without a reload; while a
// request is still moving (sent / opened / code verified / sealing) a
// fallback poll every 20 s (visible tab only). A live change that altered
// what we show is announced to the rest of the screen (caseChange
// 'statement'). E-sign refusals (the server's reason — ESIGN_OFF …) show
// under the e-sign status, not as a bare 404; the pilot off → said there.
// (D12) A paper request → the Send window on Posted or handed over; "not me"
// → a new request (Send on e-signature); the redacted evidence pack. The
// bell's statement links (statement/statementFocus) scroll to the statement
// and, for the colleague-confirm alerts, open its Send window on e-signature.
// (Big-audit fix batch 1) A request fully signed on an earlier version (a
// correction no longer cancels it) shows on the current card as "Signed on
// an earlier version (version N)"; each e-sign action's busy label / refusal
// shows on the request it was for (a 409's plain message via ErrorNote).
// (Settlement redesign) This panel now lays the screen out, because it holds the
// statements: the plain-English sentence, the next step on the left, the one
// money summary + one History area on the right (stacked on a phone). All of it
// reads statement/nextStep. Looking saves nothing: with no statement yet the
// server says what the draft WOULD be (pendingDraft) and this shows it. There is
// no default settlement date and a draft needs one, so the dates are what create
// it, in that person's name. "Start a statement" stays for a further statement
// after an interim or a proposed one. A newer draft never hides the current
// issued statement: its card folds under the draft.
// ============================================================
import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  askStatementApprovalApi, checkStatementApi, checkStatementMismatchApi, approveStatementMismatchApi, correctStatementApi, getCaseStatementsApi, getStatementDocumentApi, issueStatementApi, prepareStatementApi, saveRiskNoteApi,
  recordClientApprovalApi, recordLaterInstructionApi, approveLaterInstructionApi, refuseLaterInstructionApi, recordCourtOrderApi, recordCourtStageApi, saveStatementDraftApi, sendBackStatementApi,
  cancelStatementEsignApi, getEvidencePackApi, getSigningEvidenceApi, resendStatementEsignApi,
} from '../../api/casevoApi'
import { downloadBlob } from '../esign/esignFormat'
import type { BlockReason, CaseStatements, DraftFields, FixTarget, StatementDraft, StatementSummary } from '../../types/settlementStatement.types'
import { B_PRIMARY, B_SECONDARY, B_SECONDARY_SM, BIcon, CARD, NOTE, errorText } from '../billing/billingUi'
import { Banner } from './settlementUi'
import { ErrorNote } from '../shared/ActionBox'
import SideScroll from '../shared/SideScroll'
import { ukDate } from '../../utils/civilDate'
import { nextStepOf, sendDefault, type MoneyNow, type Setup } from './statement/nextStep'
import EsignStatus, { EarlierSignings, isEsignInProgress, isFullySigned as fullySigned, nameMismatchLine } from './statement/EsignStatus'
import { AnswerHistory } from './statement/ClientAnswer'
import ConfirmDialogue from '../shared/ConfirmDialogue'
import ModalFrame from '../shared/ModalFrame'
import DocumentPreviewFrame from '../shared/DocumentPreviewFrame'
import { buildSettlementStatementHtml, type StatementHtmlDoc } from '../../utils/settlementStatementHtml'
import { documentTheme } from '../../utils/documentTheme'
import { clientDocumentFonts } from '../../utils/documentFontFiles'
import { printCasePdf } from '../../utils/exportCasePdf'
import { emitCaseChange, useCaseChange, type CaseChangeKind } from '../../utils/caseChangeEvents'
import { useFirmChange } from '../../utils/liveEvents'
import { useCaseLive } from '../../hooks/useCaseLive'
import StatementSendModal from './StatementSendModal'
import { clearStatementFocus, onStatementFocus, peekStatementFocus } from './statement/statementFocus'
import DraftEditor from './statement/DraftEditor'
import { IssuedCard, VersionList } from './statement/IssuedStatement'
import { Fold, StepTrail, isReloadReply, stRef } from './statement/statementBits'
import { inSlot } from './statement/ClientAnswer'
import { useStickyBar } from './statement/useStickyBar'

export { StatusChip } from './statement/statementBits'
export { ClientApprovalForm, ClientAnswer, HandOff, VersionList } from './statement/IssuedStatement'
export { SEND_BACK_ID } from './statement/DraftEditor'

// The browser's copy of the statement (utils/settlementStatementHtml — the
// same builder as the issued PDF; backend parity test).
export const statementPreviewHtml = (doc: StatementHtmlDoc): string => {
  const f = doc.letterhead.firm as StatementHtmlDoc['letterhead']['firm'] & { accentColour?: string | null; secondaryColour?: string | null; headingFont?: string | null; bodyFont?: string | null }
  return buildSettlementStatementHtml(doc, documentTheme(f.accentColour, f.secondaryColour), clientDocumentFonts(f.headingFont, f.bodyFont))
}

// What changes elsewhere on the screen alters the statement's blockers /
// figures ("Figures changed after approval") — re-read on each.
// (Follow-ups) …and 'statement': sent back from the Settlement page's
// "Figures locked" box (our own announcements are skipped).
const WATCH: CaseChangeKind[] = ['settlementInputs', 'cfa', 'litigationFriend', 'clientDetails', 'funding', 'bill', 'statement']
// (E-sign fixes B) The server's live topics that change what this panel shows.
export const LIVE_TOPICS = ['settlement', 'esign'] as const
// Refused steps on the draft: said beside the buttons, not at the top of the card.
const ROW_ACTIONS = new Set(['prepare', 'check', 'ask', 'mismatchCheck', 'mismatchApprove'])
export const LIVE_POLL_MS = 20_000

// figuresUnsaved: (Audit fix batch 1, M5) something on the Settlement page is
// unsaved (a figures card, the CFA, an interim) — "Mark as prepared" waits,
// with this as the visible reason.
interface Props {
  caseId: string; onFix: (target: FixTarget) => void; figuresUnsaved?: string | null
  // The client's name for the sentence ("Travis").
  clientName?: string | null
  // Two panes (the section is wide enough) or one column.
  twoCol?: boolean
  // The one money summary for these statements, and the figures for the sentence.
  moneyOf?: (data: CaseStatements | null) => { node: React.ReactNode; now: MoneyNow | null }
  onOpenFigures?: () => void
  // A figure card is open in the drawer.
  editingFigures?: boolean
  // "Try different options" is showing in the drawer.
  tryingFigures?: boolean
  // What the case still needs before there's anything to state.
  setup?: Setup
  cfaCreate?: { run: () => void; busy: boolean; error: string | null }
}
const GRID_2 = 'grid-cols-[minmax(0,5fr)_minmax(0,4fr)]'
const historyDay = (iso: string | Date | null | undefined): string => (iso ? ukDate(String(iso instanceof Date ? iso.toISOString() : iso).slice(0, 10)) : '')

const StatementPanel: React.FC<Props> = ({ caseId, onFix, figuresUnsaved, clientName = null, twoCol = true, moneyOf, onOpenFigures, editingFigures = false, tryingFigures = false, setup = null, cfaCreate }) => {
  const [data, setData] = useState<CaseStatements | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  // The last refusal, and which action it came from (a save's refusal clears
  // as soon as a detail changes).
  const [actionError, setActionError] = useState<{ key: string; message: string } | null>(null)
  const [blockedOnIssue, setBlockedOnIssue] = useState<BlockReason[] | null>(null)
  // (Audit fix batch 1, H2) Issue asks first. (Audit B2, low b) A bill
  // difference is checked + OK'd beforehand (its own two steps), not sent with it.
  const [confirmIssue, setConfirmIssue] = useState(false)
  const [preview, setPreview] = useState<{ id: string; html: string; title: string } | null>(null)
  const [sending, setSending] = useState<StatementSummary | null>(null)
  // (D12) Which way the Send window opens (a paper request → post / hand).
  const [sendMode, setSendMode] = useState<'manual' | 'esign' | undefined>(undefined)
  // softEsign: e-signature is only the default here, so email if it can't be used.
  const [softEsign, setSoftEsign] = useState(false)
  const openSend = (s: StatementSummary, mode?: 'manual' | 'esign', soft = false): void => { setSendMode(mode); setSoftEsign(soft); setSending(s) }
  const [starting, setStarting] = useState(false)
  const [asked, setAsked] = useState<string[] | null>(null)
  // "Record the court order" starts the final statement with its order form open.
  const [orderNext, setOrderNext] = useState(false)
  // The draft that grew out of the pending one keeps the same editor (typing goes on).
  const grownFrom = useRef<string | null>(null)
  // The sticky action bar, and the page it pins to the foot of.
  const [barSlot, setBarSlot] = useState<HTMLElement | null>(null)
  const [pageEl, setPageEl] = useState<HTMLElement | null>(null)
  const { typing } = useStickyBar(pageEl, barSlot)
  // The last refused save was a conflict with someone else's change.
  const [saveConflict, setSaveConflict] = useState(false)
  // (E-sign audit fixes A) The last action’s "the old signing link wasn’t cancelled" warning.
  const [esignWarning, setEsignWarning] = useState<string | null>(null)

  // (Audit fix batch 1, M6) Only the newest read's answer is used, and a
  // write's own reply outranks every read that started before it came back —
  // a slow event-driven re-read can't show "not prepared" after Prepare.
  const seq = useRef(0)
  const load = useCallback(async () => {
    const n = ++seq.current
    try { const r = await getCaseStatementsApi(caseId); if (n !== seq.current) return; setData(r); setLoadError(null) } catch (e) { if (n === seq.current) setLoadError(errorText(e, 'Couldn’t load the settlement statements.')) }
  }, [caseId])
  const applyReply = useCallback((r: CaseStatements): void => { seq.current++; setData(r) }, [])
  useEffect(() => { void load() }, [load])
  const ownEmit = useRef(false)
  const tell = useCallback((kind: CaseChangeKind) => {
    ownEmit.current = true
    try { emitCaseChange(caseId, kind) } finally { ownEmit.current = false }
  }, [caseId])
  useCaseChange(caseId, WATCH, (_k, info) => {
    if (ownEmit.current) return
    // (D12) A remote settlement / e-sign change is already re-read by useCaseLive below.
    if (info.remote && !info.resync && info.topics?.length && info.topics.every((t) => (LIVE_TOPICS as readonly string[]).includes(t))) return
    void load()
  })
  // (E-sign fixes B) Live: another user / the signer / the sealing job changed
  // something. A quiet re-read (a failed one keeps what's shown); when it
  // changed what we show, the rest of the screen is told.
  const dataRef = useRef<CaseStatements | null>(null)
  dataRef.current = data
  const liveLoad = useCallback(async () => {
    const n = ++seq.current
    try {
      const r = await getCaseStatementsApi(caseId)
      if (n !== seq.current) return
      const changed = !!dataRef.current && JSON.stringify(dataRef.current) !== JSON.stringify(r)
      setData(r); setLoadError(null)
      if (changed) tell('statement')
    } catch { /* keep what's shown; the next signal / poll tries again */ }
  }, [caseId, tell])
  const inProgress = Object.values(data?.esign ?? {}).some((e) => isEsignInProgress(e))
  useCaseLive(caseId, LIVE_TOPICS, () => { void liveLoad() }, { pollMs: LIVE_POLL_MS, pollWhile: inProgress })
  // An admin changed Two-person checks: who may approve changes at once.
  useFirmChange(['settings'], () => { void liveLoad() })
  // (D12) The bell asked for a statement: scroll to it (the current one, else
  // the panel) and, for the colleague-confirm alerts, open Send on e-signature.
  const [focusNonce, setFocusNonce] = useState(0)
  useEffect(() => onStatementFocus(() => setFocusNonce((n) => n + 1)), [])
  useEffect(() => {
    const f = peekStatementFocus(caseId)
    if (!f || !data) return
    clearStatementFocus()
    const s = data.statements.find((x) => x.id === f.statementId) ?? null
    const el = (s && document.querySelector<HTMLElement>(`[data-statement-id="${s.id}"]`)) || document.getElementById('settlement-statement')
    el?.scrollIntoView?.({ block: 'start', behavior: 'smooth' })
    el?.focus?.({ preventScroll: true })
    if (s && f.openEsign && data.esignPilot && s.status !== 'DRAFT') { setSendMode('esign'); setSending(s) }
  }, [caseId, data, focusNonce])

  // One wrapper for every action: busy, error, the fresh list back (or a
  // re-read), then the rest of the screen is told.
  const run = async (key: string, fn: () => Promise<CaseStatements>, kind: CaseChangeKind = 'statement'): Promise<boolean> => {
    setBusy(key); setActionError(null); setBlockedOnIssue(null); setEsignWarning(null)
    try {
      const r = await fn()
      if (r.esignRevokeWarning) setEsignWarning(r.esignRevokeWarning)
      if (isReloadReply(r)) await load(); else applyReply(r)
      tell(kind)
      return true
    } catch (e) {
      const res = (e as { response?: { status?: number; data?: { blockers?: BlockReason[] } } }).response
      if (res?.data?.blockers?.length) setBlockedOnIssue(res.data.blockers)
      setActionError({ key, message: errorText(e, 'That didn’t work. Please try again.') })
      setSaveConflict(key === 'save' && res?.status === 409)
      // Changed meanwhile (another user, the figures moved, or it's locked): show it as it is now.
      if (res?.status === 409) void load()
      return false
    } finally { setBusy(null) }
  }
  // (B4c 1b) E-signature actions — each reloads the statements after.
  const esignDo = async (key: string, fn: () => Promise<void>, fallback: string): Promise<boolean> => {
    setBusy(key); setActionError(null)
    try { await fn(); await load(); tell('statement'); return true } catch (e) {
      setActionError({ key, message: errorText(e, fallback) }); void load(); return false
    } finally { setBusy(null) }
  }
  // (Big-audit fix batch 1) Which statement's request the last e-sign action
  // was for — its busy label and refusal show on THAT request's status (the
  // current version's, or one signed on an earlier version).
  const [esignTarget, setEsignTarget] = useState<string | null>(null)
  const esignActionsFor = (s: StatementSummary) => {
    const e = data?.esign?.[s.id]
    const on = <A extends unknown[], R>(fn: (...a: A) => R) => (...a: A): R => { setEsignTarget(s.id); return fn(...a) }
    return {
      // A 409 (e.g. every signer has now signed) → the server's plain message under the status, then a re-read.
      resend: on(() => { void esignDo('esign:resend', async () => { await resendStatementEsignApi(caseId, s.id) }, 'The link wasn’t resent.') }),
      cancel: on((reason: string) => esignDo('esign:cancel', () => cancelStatementEsignApi(caseId, s.id, reason), 'The request wasn’t cancelled.')),
      viewSigned: on(() => {
        if (!e?.signedPdfId) return
        setBusy('esign:pdf')
        getSigningEvidenceApi(e.id, e.signedPdfId)
          .then((b) => { const url = URL.createObjectURL(new Blob([b], { type: 'application/pdf' })); window.open(url, '_blank', 'noopener'); setTimeout(() => URL.revokeObjectURL(url), 60_000) })
          .catch((err) => setActionError({ key: 'esign:pdf', message: errorText(err, 'Couldn’t open the signed PDF.') }))
          .finally(() => setBusy(null))
      }),
      evidencePack: on(() => pack(false)),
      // (D12) IP addresses, devices and reasons removed — for the other side.
      redactedPack: on(() => pack(true)),
      sendPaper: () => openSend(s, 'manual'),
      sendNew: () => openSend(s, 'esign'),
    }
    function pack(redacted: boolean): void {
      if (!e) return
      const key = redacted ? 'esign:pack-redacted' : 'esign:pack'
      setBusy(key)
      getEvidencePackApi(e.id, { redacted })
        .then((b) => downloadBlob(b, `Evidence pack ${e.reference}${redacted ? ' (redacted)' : ''}.zip`))
        .catch((err) => setActionError({ key, message: errorText(err, 'Couldn’t make the evidence pack.') }))
        .finally(() => setBusy(null))
    }
  }
  const clearSaveError = (): void => { setActionError((a) => (a && a.key === 'save' ? null : a)) }
  // Create the draft from what the server said it would be, plus any change.
  const createFrom = async (p: StatementDraft, change: DraftFields = {}): Promise<StatementDraft | null> => {
    const { baseUpdatedAt: _none, ...fields } = change
    let made: StatementDraft | null = null
    const ok = await run('save', async () => {
      const r = await saveStatementDraftApi(caseId, {
        settlementDate: p.settlementDate ?? undefined, damagesReceivedAt: p.damagesReceivedAt, kind: p.kind, retentionPence: p.retentionPence, retentionReason: p.retentionReason, netDestinationDetail: p.netDestinationDetail, signatoryUserId: p.signatoryUserId, proposed: p.proposed, ...fields,
      })
      made = r.draft ?? null
      grownFrom.current = r.draft?.id ?? null
      return r
    })
    return ok ? made : null
  }
  // The approve step, then (when nothing else stands in the way) issue: the same
  // three routes, each refusing what it always did; each stops the next.
  const approveFlow = async (id: string, fingerprint: string | undefined, review: Parameters<typeof checkStatementApi>[3], needPrepare: boolean, andIssue: boolean | undefined): Promise<boolean> => {
    if (needPrepare && !(await run('prepare', () => prepareStatementApi(caseId, id)))) return false
    if (!(await run('check', () => checkStatementApi(caseId, id, fingerprint, review)))) return false
    return andIssue ? run('issue', () => issueStatementApi(caseId, id)) : true
  }
  const saveRisk = async (note: string): Promise<string | null> => {
    try {
      const r = await saveRiskNoteApi(caseId, note)
      if (isReloadReply(r)) await load(); else applyReply(r)
      tell('cfa')
      return null
    } catch (e) { void load(); return errorText(e, 'The note wasn’t saved. Please try again.') }
  }
  const openPreview = async (s: StatementSummary): Promise<void> => {
    setBusy(`preview:${s.id}`); setActionError(null)
    try {
      const r = await getStatementDocumentApi(caseId, s.id)
      setPreview({ id: s.id, html: statementPreviewHtml(r.doc), title: `Statement ${stRef(s)}` })
    } catch (e) { setActionError({ key: 'preview', message: errorText(e, 'Couldn’t load the statement.') }) } finally { setBusy(null) }
  }

  // The money never waits for the statements.
  if (loadError) return <div className="flex flex-col gap-4"><Banner tone="error" action={<button type="button" className={B_SECONDARY_SM} onClick={() => { void load() }}>Retry</button>}>{loadError}</Banner>{moneyOf?.(null).node}</div>
  if (!data) return <div className="flex flex-col gap-4"><div className={`${CARD} p-5 text-[13px] text-casevo-text-soft`} aria-busy="true">Loading the settlement statement…</div>{moneyOf?.(null).node}</div>
  // Nothing saved yet: the page reads the draft the server says it would be.
  const pending = !data.draft && !!data.pendingDraft
  const view: CaseStatements = pending ? { ...data, draft: data.pendingDraft ?? null } : data
  const draft = view.draft
  const issued = data.statements.filter((s) => s.status !== 'DRAFT')
  const current = issued.find((s) => s.status === 'ISSUED' || s.status === 'APPROVED') ?? null
  // (Big-audit fix batch 1) A request every signer signed on an EARLIER
  // version stays signed after a correction — shown on the current card as
  // "Signed on an earlier version (version N)", packs still available.
  // (Big-audit fix batch 2) …every one of them (folded on the card once the current version is signed).
  const earlier = current ? issued
    .filter((s) => s.version < current.version && fullySigned(data.esign?.[s.id]))
    .sort((a, b) => b.version - a.version) : []
  const esignBusyFor = (id: string): string | null => (busy?.startsWith('esign:') && esignTarget && esignTarget !== id ? 'esign:other' : busy)
  const esignErrorFor = (id: string): string | null => (actionError?.key.startsWith('esign:') && (!esignTarget || esignTarget === id) ? actionError.message : null)

  const money = moneyOf ? moneyOf(view) : null
  const worked = nextStepOf({ data: view, name: clientName, figures: money?.now ?? null, setup })
  // While a figure is being changed, that is the next thing.
  const step = draft && tryingFigures ? { ...worked, next: 'You’re trying different figures. Next: go back to the saved figures.' }
    : draft && (figuresUnsaved || editingFigures) ? { ...worked, next: 'You’re changing the figures. Next: save or cancel.' } : worked
  const currentStep = current ? nextStepOf({ data: { ...data, draft: null, pendingDraft: null }, name: clientName, figures: null }) : null
  // No statement to read yet: the main button is here. Rendered on its own (no money
  // column to check against), it's simply "Start a statement".
  const mainNow = step.main?.action === 'checkFigures' && !onOpenFigures ? 'start' : step.main?.action
  const mainLabel = mainNow === 'start' ? 'Start a statement' : step.main?.label
  const pressEmptyMain = (): void => {
    if (mainNow === 'createCfa') cfaCreate?.run()
    else if (mainNow === 'addFigures') onFix('settlementInputs')
    else if (mainNow === 'checkFigures') onOpenFigures?.()
    else setStarting(true)
  }
  const allIssuedNewestFirst = [...issued].sort((x, y) => y.version - x.version)
  const historyOf = current ?? draft
  const answers = current?.answerHistory ?? []
  const earlierCards = earlier.flatMap((x) => {
    const e = data.esign?.[x.id]
    if (!e) return []
    const acts = esignActionsFor(x)
    return [<EsignStatus key={e.id} e={e} busy={esignBusyFor(x.id)} earlierVersion={x.version} onResend={acts.resend} onCancel={acts.cancel} onViewSigned={acts.viewSigned}
      onEvidencePack={acts.evidencePack} onRedactedPack={acts.redactedPack} pilotOff={!data.esignPilot} error={esignErrorFor(x.id)} />]
  })

  const issuedCard = current && currentStep ? (
    <IssuedCard s={current} data={data} busy={busy} step={draft ? currentStep : step}
      preview={() => { void openPreview(current) }}
      send={() => openSend(current, sendDefault(data, current), true)}
      correct={() => run('correct', () => correctStatementApi(caseId, current.id))}
      startFinal={() => { setOrderNext(true); void run('correct', () => correctStatementApi(caseId, current.id)) }}
      approve={(body) => run('approval', () => recordClientApprovalApi(caseId, current.id, body))}
      caseId={caseId}
      later={{
        record: (body) => run('later', () => recordLaterInstructionApi(caseId, current.id, body)),
        approve: (insId) => run('laterApprove', () => approveLaterInstructionApi(caseId, current.id, insId)),
        refuse: (insId, reason) => run('laterRefuse', () => refuseLaterInstructionApi(caseId, current.id, insId, reason)),
      }}
      recordStage={(body) => run('stage', () => recordCourtStageApi(caseId, current.id, body), 'courtOrder')}
      esignActions={esignActionsFor(current)} esignPilotOff={!data.esignPilot}
      esignError={esignErrorFor(current.id)} esignBusy={esignBusyFor(current.id)}
      hasDraft={!!draft} barSlot={barSlot} />
  ) : null
  const moneyAndHistory = (
    <div className="flex flex-col gap-4">
      {money?.node}
      <Fold className={`${CARD} px-5 py-3`} labelClass="text-[13px] font-semibold text-casevo-text-soft" label="History" dataKey="history">
        <div className="flex flex-col gap-4">
          {!historyOf?.prepared && !historyOf?.issuedAt && issued.length === 0 && <p className={`${NOTE} m-0`}>Nothing yet.</p>}
          {historyOf && (historyOf.prepared || historyOf.checked || historyOf.issuedAt) && (
            <dl className="m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-[12px] gap-y-[4px] text-[13px]" aria-label="What happened to this statement" data-history-steps="">
              {historyOf.prepared && <><dt className="text-casevo-text-soft">Prepared</dt><dd className="m-0">{historyOf.prepared.by}, {historyDay(historyOf.prepared.at)}</dd></>}
              {historyOf.checked && <><dt className="text-casevo-text-soft">Approved</dt><dd className="m-0">{historyOf.checked.by}, {historyDay(historyOf.checked.at)}{historyOf.notesReviewed ? ' · notes reviewed' : ''}</dd></>}
              {historyOf.issuedAt && <><dt className="text-casevo-text-soft">Issued</dt><dd className="m-0">{historyOf.issuedBy ?? 'A former member'}, {historyDay(historyOf.issuedAt)}</dd></>}
              {historyOf.delivered && <><dt className="text-casevo-text-soft">Delivered</dt><dd className="m-0">{historyOf.delivered.by}, {historyDay(historyOf.delivered.at)}</dd></>}
            </dl>
          )}
          {allIssuedNewestFirst.length > 0 && <VersionList statements={allIssuedNewestFirst} busy={busy} representativeType={data.representativeType} onPreview={(s) => { void openPreview(s) }} />}
          {earlierCards.length > 0 && (fullySigned(current ? data.esign?.[current.id] : null)
            ? <EarlierSignings count={earlier.length} versions={earlier.map((x) => x.version)} mismatch={earlier.some((x) => { const e = data.esign?.[x.id]; return !!e && !!nameMismatchLine(e) })}>{earlierCards}</EarlierSignings>
            : earlierCards)}
          {answers.length > 0 && <AnswerHistory entries={answers} caseId={caseId} />}
        </div>
      </Fold>
    </div>
  )

  return (
    <div ref={setPageEl} className="flex flex-col gap-4" data-statement-page="">
      <section aria-label="Where things stand" data-sentence="" className="rounded-[14px] border border-casevo-border bg-casevo-surface px-5 py-4">
        <span className="inline-flex items-center px-2 py-[2px] rounded-[6px] border border-casevo-border-soft bg-casevo-input text-[11px] font-bold uppercase tracking-[0.06em] text-casevo-text-soft" data-sentence-tag="">{step.tag}</span>
        {step.stands && <p className="m-0 mt-[6px] text-[18px] sm:text-[21px] leading-[1.32] font-semibold tracking-[-0.01em] text-casevo-forest" data-sentence-stands="">{step.stands}</p>}
        {step.next && <p className="m-0 mt-[2px] text-[15px] sm:text-[17px] leading-[1.35] font-semibold text-casevo-brand dark:text-casevo-link" data-sentence-next="">{step.next}</p>}
      </section>

      <div className={`grid grid-cols-1 ${twoCol ? GRID_2 : ''} gap-4 items-start`}>
        <section aria-label="Settlement statement" className={`${CARD} p-5 min-w-0`}>
          <div className="flex flex-col gap-4" id="settlement-statement">
            {/* (M7) A refused send-back is said inside its dialog, not here behind it. */}
            {actionError && !blockedOnIssue && actionError.key !== 'sendBack' && !ROW_ACTIONS.has(actionError.key) && !actionError.key.startsWith('esign:') && !(actionError.key === 'save' && draft) && <Banner tone="error">{actionError.message}</Banner>}
            {esignWarning && <div data-esign-revoke-warning=""><Banner tone="warn" title="The old version’s signing link is still open">{esignWarning}</Banner></div>}
            {!data.wording.ok && <Banner tone="warn" action={<button type="button" className={B_SECONDARY_SM} onClick={() => onFix('settlementWording')}>Open Settlement wording</button>}>{data.wording.reason}</Banner>}

            {draft ? (
              <DraftEditor key={pending || draft.id === grownFrom.current ? 'grown' : draft.id} data={view} draft={draft} busy={busy} onFix={onFix} onEdit={clearSaveError} blockedOnIssue={blockedOnIssue} step={step} saveRiskNote={saveRisk} openOrderAtStart={orderNext}
                // (Audit fix batch 1, H3) The body carries the version the editor
                // OPENED from (baseUpdatedAt) and only the fields that changed.
                save={async (body) => (pending ? !!(await createFrom(draft, body)) : run('save', () => saveStatementDraftApi(caseId, body)))}
                prepare={() => run('prepare', () => prepareStatementApi(caseId, draft.id))}
                prepareAndApprove={(review, andIssue) => approveFlow(draft.id, draft.approvalFingerprint, review, true, andIssue)}
                saveRefused={actionError?.key === 'save' ? actionError.message : null} saveConflict={saveConflict} reload={load} barSlot={barSlot}
                askApproval={async () => {
                  setBusy('ask'); setActionError(null)
                  try { setAsked((await askStatementApprovalApi(caseId, draft.id)).asked); return true } catch (e) {
                    setActionError({ key: 'ask', message: errorText(e, 'Couldn’t ask for approval.') }); return false
                  } finally { setBusy(null) }
                }}
                asked={asked}
                actionError={actionError && ROW_ACTIONS.has(actionError.key) ? actionError.message : null}
                check={(review, andIssue) => approveFlow(draft.id, draft.approvalFingerprint, review, false, andIssue)}
                issue={() => setConfirmIssue(true)}
                checkMismatch={pending ? undefined : (reason) => run('mismatchCheck', () => checkStatementMismatchApi(caseId, draft.id, { checked: true, reason, key: draft.billMismatch?.key ?? '' }))}
                approveMismatch={pending ? undefined : () => run('mismatchApprove', () => approveStatementMismatchApi(caseId, draft.id, draft.billMismatch?.key ?? ''))}
                figuresUnsaved={figuresUnsaved ?? null}
                preview={pending ? undefined : () => { void openPreview(draft) }}
                recordOrder={pending ? undefined : (body) => run('order', () => recordCourtOrderApi(caseId, draft.id, body), 'courtOrder')}
                recordStage={pending ? undefined : (body) => run('stage', () => recordCourtStageApi(caseId, draft.id, body), 'courtOrder')}
                sendBack={(reason) => run('sendBack', () => sendBackStatementApi(caseId, draft.id, reason))}
                sendBackError={actionError?.key === 'sendBack' ? actionError.message : null} />
            ) : starting ? (
              <DraftEditor data={data} draft={null} busy={busy} onFix={onFix} onEdit={clearSaveError} barSlot={barSlot}
                save={async (body) => { const ok = await run('save', () => saveStatementDraftApi(caseId, body)); if (ok) setStarting(false); return ok }}
                onCancel={() => { setStarting(false); clearSaveError() }} />
            ) : current ? null : (
              <>
                <div className="flex flex-col gap-[8px]">
                    <p className={`${NOTE} m-0`} role={busy === 'save' ? 'status' : undefined}>{busy === 'save' ? 'Setting up the statement…'
                      : mainNow === 'createCfa' ? 'Every deduction comes from the CFA. This one starts from the firm’s standard terms; check it against what the client signed.'
                        : mainNow === 'addFigures' ? 'The statement sets itself up as soon as the figures are in.'
                          : mainNow === 'checkFigures' ? 'Something in the figures needs sorting out first. The money column says what.'
                            : issued.length ? 'Start another statement (for example the final one after an interim), or correct the current one below.' : 'No statement yet. Start one when the damages are agreed.'}</p>
                    {mainNow === 'createCfa' && cfaCreate?.error && <ErrorNote>{cfaCreate.error}</ErrorNote>}
                </div>
                {inSlot(barSlot, <div className="flex flex-col gap-[8px]" data-main-area="">
                  <div className="flex flex-wrap-reverse items-center gap-x-[12px] gap-y-[4px]" data-main-row="">
                    <button type="button" className={B_PRIMARY} data-main-action={mainNow} disabled={busy === 'save' || (mainNow === 'createCfa' && cfaCreate?.busy)} onClick={pressEmptyMain}>{mainNow === 'createCfa' && cfaCreate?.busy ? 'Creating…' : mainLabel}</button>
                  </div>
                  <StepTrail trail={step.trail} />
                </div>)}
              </>
            )}

            {!draft && issuedCard}
            {current && !draft && !starting && (current.kind !== 'FINAL' || current.proposed) && <div><button type="button" className="text-[13px] font-semibold text-casevo-link focus:outline-none casevo-focus rounded-[4px] hover:underline underline-offset-2" onClick={() => setStarting(true)}>Start a statement</button></div>}
            {current && draft && !pending && (
              <Fold className="rounded-[10px] border border-casevo-border px-[12px] py-[10px]" labelClass="text-[13.5px] font-semibold text-casevo-forest" dataKey="current-issued" openKey={focusNonce}
                label={`Current issued statement (v${current.version})`}>
                {issuedCard}
              </Fold>
            )}
          </div>
        </section>

        <div className={`${twoCol ? 'sticky top-4 self-start' : ''} min-w-0`}>
          {twoCol ? <SideScroll label="The money and history">{moneyAndHistory}</SideScroll> : moneyAndHistory}
        </div>
      </div>

      {/* The action bar: last on the page so nothing ever ends up behind it. Pinned to
          the foot of the visible area, over the left column when there are two; on a
          phone, to the foot of the screen (the page keeps room for it at its end). */}
      <div ref={setBarSlot} data-action-bar="" className={`${typing ? 'hidden' : ''} fixed inset-x-0 bottom-0 md:sticky md:bottom-3 z-20 md:mt-auto min-w-0 ${twoCol ? 'md:w-[calc((100%-16px)*5/9)]' : ''} border-t md:border md:rounded-[14px] border-casevo-border bg-casevo-surface shadow-[0_-6px_18px_rgba(16,42,29,0.08)] dark:shadow-[0_-6px_18px_rgba(0,0,0,0.3)] px-4 pt-[10px] pb-[calc(10px+env(safe-area-inset-bottom))] empty:hidden`} />

      {confirmIssue && draft && (
        <ConfirmDialogue isOpen title={`Issue statement ${draft.number ?? ''} v${draft.version}?`.replace('  ', ' ')}
          message="This gives the statement its number, freezes the figures, the wording and the signatory, and saves the PDF to the case. An issued statement can’t be changed — a correction is a new version."
          confirmLabel="Issue statement"
          onCancel={() => setConfirmIssue(false)}
          onConfirm={() => { setConfirmIssue(false); void run('issue', () => issueStatementApi(caseId, draft.id)) }} />
      )}
      {preview && (
        <ModalFrame size="xxl" onClose={() => setPreview(null)} eyebrow="Settlement statement" title={preview.title}
          footerRight={<><button type="button" className={B_SECONDARY} onClick={() => setPreview(null)}>Close</button>
            <button type="button" className={B_PRIMARY} onClick={() => printCasePdf(preview.html)}><BIcon name="print" size={13} />Print / save PDF</button></>}>
          <DocumentPreviewFrame title={preview.title} html={preview.html} />
        </ModalFrame>
      )}
      {/* (Audit fix batch 1, Low 13) ONE announcement per send (the window
          itself announces nothing): the statement, and the bill when one went with it. */}
      {sending && <StatementSendModal caseId={caseId} statement={sending} esignPilot={!!data.esignPilot} onFix={onFix} initialMode={sendMode} softEsign={softEsign} onClose={() => setSending(null)} onDone={(d, withBill) => { setSending(null); if (d && !isReloadReply(d)) applyReply(d); else void load(); tell('statement'); if (withBill) tell('bill') }} />}
    </div>
  )
}

export default StatementPanel
